import React, { useEffect, useState } from 'react';
import { Box, Flex, Image, Link, Skeleton, Stack } from '@chakra-ui/react';
import './AuthPage.scss';
import AuthLogo from '../../components/AuthLogo/AuthLogo.tsx';
import LoginForm from '../../components/LoginForm/LoginForm.tsx';
import PoweredBy from '../../components/PoweredBy/PoweredBy.tsx';
import ForgotPasswordForm from '../../components/ForgotPasswordForm/ForgotPasswordForm.tsx';
import authBackground from '/assets/images/backgrounds/auth-bg.png';
import arrowLeft from '/assets/images/icons/arrow-left.svg';
import ChangePasswordForm from '../../components/ChangePasswordForm/ChangePasswordForm.tsx';
import SetPasswordForm from '../../components/SetPasswordForm/SetPasswordForm.tsx';
import AuthLanguageSwitcher from '../../components/AuthLanguageSwitcher/AuthLanguageSwitcher.tsx';
import { useParams, useLocation } from 'react-router-dom';
import AuthService from '../../services/AuthService.ts';

const AuthPage: React.FC = () => {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const isRegisterPage = location.pathname.includes('/register/');

  useEffect(() => {
    // Add a class to the body when the component is mounted
    document.body.classList.add('auth-page-active');

    // Cleanup function to remove the class when the component is unmounted
    return () => {
      document.body.classList.remove('auth-page-active');
    };
  }, []);

  useEffect(() => {
    const checkToken = async () => {
      if (token) {
        setIsLoading(true);
        try {
          await AuthService.checkPasswordResetToken(token);
          
          // Decide which form to show based on the pathname
          if (location.pathname.includes('/register/')) {
            setShowSetPassword(true);
          } else if (location.pathname.includes('/reset-password/')) {
            setShowChangePassword(true);
          }
        } catch (error) {
          console.error('Invalid token:', error);
          setIsTokenValid(false);
          
          // If token is invalid, show the SetPasswordForm for registration
          if (location.pathname.includes('/register/')) {
            setShowSetPassword(true);
          } else if (location.pathname.includes('/reset-password/')) {
            setShowChangePassword(true);
          }
        } finally {
          setIsLoading(false);
        }
      }
    };

    checkToken();
  }, [token, location.pathname]);

  /**
   * Handles the "Forgot Password" link click.
   * Sets the state to show the ForgotPassword component.
   * @returns {void}
   */
  const handleForgotPasswordClick = (): void => {
    setShowForgotPassword(true);
  };

  /**
   * Handles the "Back to Log in" link click.
   * Sets the state to show the LoginForm component.
   * @returns {void}
   */
  const handleGoBackClick = (): void => {
    setShowForgotPassword(false);
    setShowChangePassword(false);
    setShowSetPassword(false);
  };

  return (
    <Flex className="auth-page">
      <Box className="auth-language-switcher-wrapper">
        <AuthLanguageSwitcher />
      </Box>
      <Box className={`auth-left ${isRegisterPage ? 'auth-left-register' : ''}`}>
        <Box px={6} className="auth-left-content">
          <Box className="auth-logo-container">
            <AuthLogo />
          </Box>

          {isLoading ? (
            <Stack spacing={4} mt={6} width="100%">
              <Skeleton height="40px" />
            </Stack>
          ) : showSetPassword ? (
            <SetPasswordForm
              onBackToLoginClick={handleGoBackClick}
              isTokenValid={isTokenValid}
            />
          ) : showChangePassword ? (
            <ChangePasswordForm
              onBackToLoginClick={handleGoBackClick}
              isTokenValid={isTokenValid}
            />
          ) : showForgotPassword ? (
            <ForgotPasswordForm onBackToLoginClick={handleGoBackClick} />
          ) : (
            <LoginForm onForgotPasswordClick={handleForgotPasswordClick} />
          )}
        </Box>
        <PoweredBy />
      </Box>
      <Box
        className="auth-right"
        style={{ backgroundImage: `url(${authBackground})` }}
      ></Box>

      {/*go back*/}
      {showForgotPassword && (
        <div className="auth-go-back">
          <Link onClick={handleGoBackClick} className="auth-go-back-link">
            <Image src={arrowLeft} />
          </Link>
        </div>
      )}
    </Flex>
  );
};

export default AuthPage;
